import {useNavigate, useParams} from "react-router-dom";
import {PageHeader} from "../../../components/PageHeader";
import React, {useEffect, useMemo, useState} from "react";
import ApprovalComponent from "../../settings/approval/new/ApprovalComponent";
import Th_title from "../../../components/Th_title";
import {getEvaluationReport} from "../../../api/evaluation/ReportService";
import ReadOnlyInputBasic from "../../../components/input/ReadOnlyInputBasic";
import {skeleton} from "../../../redux/slice/apiSlice";
import {useDispatch} from "react-redux";
import {ContentCategory} from "../../../constant/CodeSpecNum";
import {
    EVALUATION_INCIDENT_LIST_HEADER,
    EVALUATION_INSPECTION_LIST_HEADER,
    EVALUATION_SERVICE_LIST_HEADER,
    EVALUATION_WORK_LIST_HEADER,
} from "../../../components/table/header/Headers";
import SimmsDatePicker from "../../../components/datepicker/SimmsDatePicker";
import {cn} from "../../../shadCn/lib/utils";
import SignContainer from "../../../components/basic/SignContainer";
import {
    ContentDetailTwoColLayout,
    DetailKvTable,
    DetailListPage,
    DetailPanel,
    DetailSummaryListButton,
    DetailSummaryPrintButton,
} from "../../../components/detailLayout";
import "../../../components/detailLayout/detailListLayout.css";
import {SettingsContentHeroActions} from "../../settings/shared/SettingsContentHeroActions";
import {renderReportCell, resolveReportColWidth} from "./evaluationReportTableShared";

function EvaluationReportView() {
    const {type, id} = useParams();

    const [report, setReport] = useState({});
    const [list, setList] = useState();
    const [code, setCode] = useState();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const title = {
        header: type === "inspection" ? "점검종합보고서" : type === "incident" ? "장애종합보고서" : type === "work" ? "작업종합보고서" : "요청종합보고서",
        fieldName: type === "inspection" ? "점검종류" : type === "incident" ? "장애종류" : type === "work" ? "작업종류" : "접수방법",
        indicator: type === "inspection" ? "평가관리/보고서 > 업무보고서 > 점검종합보고서" :
            type === "incident" ? "평가관리/보고서 > 업무보고서 > 장애종합보고서" :
                type === "work" ? "평가관리/보고서 > 업무보고서 > 작업종합보고서" :
                    "서비스요청 > 요청종합보고서",
    };

    useEffect(() => {
        dispatch(skeleton(true));
        getEvaluationReport(id, type).then((r) => {
            if (r.status === 201) {
                if (r.data.errorCode === "AUTHORITY") {
                    alert(r.data.reason);
                    navigate(-1);
                }
            }

            setReport(r.data.data);
            setCode(r.data.code);
            setList(r.data.list);
        }).finally(() => dispatch(skeleton(false)));
    }, [type, id, dispatch, navigate]);

    const headerColumns = type === "inspection" ? EVALUATION_INSPECTION_LIST_HEADER :
        type === "incident" ? EVALUATION_INCIDENT_LIST_HEADER :
            type === "work" ? EVALUATION_WORK_LIST_HEADER :
                EVALUATION_SERVICE_LIST_HEADER;

    const headerActions = (
        <SettingsContentHeroActions>
            <DetailSummaryPrintButton/>
            <DetailSummaryListButton url={`/evaluation/report/${type}`}>목록</DetailSummaryListButton>
        </SettingsContentHeroActions>
    );

    const summaryPanel = useMemo(() => (
        <DetailPanel title="월간보고서">
            <DetailKvTable>
                <tbody>
                <tr>
                    <Th_title title={`선택된 ${title.fieldName}`} colspan={1} className="fieldset-td">
                        <ReadOnlyInputBasic value={code?.[0]?.name}/>
                    </Th_title>
                    <Th_title title="보고서 제목" colspan={1} className="fieldset-td">
                        <ReadOnlyInputBasic value={report.name}/>
                    </Th_title>
                </tr>
                <tr>
                    <Th_title title="작성 기간" colspan={3} className="fieldset-td">
                        <div className="web-flex items-center">
                            <SimmsDatePicker data={report.startDate} readonly/>
                            <span className={cn("ml-2 mr-1 text-[20px]")}> ~ </span>
                            <SimmsDatePicker data={report.endDate} readonly/>
                        </div>
                    </Th_title>
                </tr>
                </tbody>
            </DetailKvTable>
        </DetailPanel>
    ), [code, report.endDate, report.name, report.startDate, title.fieldName]);

    const listPanel = useMemo(() => (
        list ? (
            <DetailPanel title={title.header}>
                <div className="evaluation-report-detail-table-wrap overflow-x-auto">
                    <table className="w-full table-border-gary evaluation-report-data-table" style={{tableLayout: "fixed"}}>
                        <colgroup>
                            <col style={{width: "8%"}}/>
                            {headerColumns.map((col, index) => (
                                <col key={index} style={{width: resolveReportColWidth(col)}}/>
                            ))}
                        </colgroup>
                        <thead>
                        <tr>
                            <th className="common-table-th-main-color text-center">No.</th>
                            {headerColumns.map((col, index) => (
                                <th key={index} className="common-table-th-main-color text-indent text-left">
                                    {col.name}
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {list.map((item, index) => (
                            <tr key={index}>
                                <td
                                    className="text-indent text-center pt-1.5 pb-1.5"
                                    style={{border: "1px solid #e5e5e5"}}
                                >
                                    {index + 1}
                                </td>
                                {headerColumns.map((col, idx) => (
                                    <td
                                        key={idx}
                                        className={`text-indent text-left pt-1.5 pb-1.5 align-top${Array.isArray(col.field) ? "" : " truncate"}`}
                                        style={{border: "1px solid #e5e5e5"}}
                                    >
                                        {renderReportCell(item, col)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </DetailPanel>
        ) : null
    ), [headerColumns, list, title.header]);

    if (Object.keys(report).length === 0) {
        return null;
    }

    return (
        <div className="common-container container-height-full border-l-gray detail-list-layout asset-detail-list-layout flex min-h-0 flex-1 flex-col">
            <PageHeader
                header={title.header}
                indicator={`${title.indicator} > 상세`}
                headerActions={headerActions}
            />

            <DetailListPage className="flex min-h-0 flex-1 flex-col">
                <div className="container-height-body asset-detail-body detail-list-body flex min-h-0 flex-1 flex-col overflow-hidden">
                    <div className="asset-detail-list-scroll detail-list-scroll detail-list-scroll--v2-cols">
                        <ContentDetailTwoColLayout
                            main={
                                <>
                                    {summaryPanel}
                                    {listPanel}
                                </>
                            }
                            aside={
                                <>
                                    <SignContainer
                                        contentId={report.id}
                                        contentCategory={ContentCategory.EVALUATION_REPORT}
                                    />
                                    <ApprovalComponent
                                        contentId={report.id}
                                        contentCategory={ContentCategory.EVALUATION_REPORT}
                                    />
                                </>
                            }
                        />
                    </div>
                </div>
            </DetailListPage>
        </div>
    );
}

export {EvaluationReportView};
